import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProductAcpFeedPreviewResponse } from "reedy-data/api";
import { Badge } from "~/components/ui/badge";
import { Text } from "~/components/text";

import { useChildSectionState } from "~/pages/product-details/tabs/raw-data-tab/hooks/use-child-section-state";
import { DataSection } from "./data-section";
import { ToggleAllButton } from "./toggle-all-button";
import { humanizeKey, renderFields } from "./render-fields";

interface AcpFeedPreviewSectionProps {
  data: ProductAcpFeedPreviewResponse;
}

export const AcpFeedPreviewSection = ({ data }: AcpFeedPreviewSectionProps) => {
  const { t } = useTranslation(["productDetails"]);
  const { acp_product } = data;
  const product = acp_product as unknown as Record<string, unknown>;

  const { variants, ...rest } = product;
  const variantArray = useMemo(
    () => (Array.isArray(variants) ? variants : []),
    [variants],
  );

  const childKeys = useMemo(() => {
    const fieldKeys = Object.keys(rest).filter((k) => {
      const v = rest[k];
      return v !== null && v !== undefined && v !== "";
    });
    const variantKeys = variantArray.map((_, i) => `variant-${i}`);
    return [...fieldKeys, ...variantKeys];
    // biome-ignore lint/correctness/useExhaustiveDependencies: rest is rebuilt each render; recomputing the key list is cheap
  }, [rest, variantArray]);

  const { openState, allOpen, toggle, toggleAll } =
    useChildSectionState(childKeys);

  return (
    <div className="flex flex-col gap-1">
      <DataSection
        title={t("productDetails:rawData.acp.eligibility")}
        testId="section-acp-eligibility"
      >
        <div className="flex gap-4 py-2">
          <div className="flex items-center gap-2">
            <Text as="span" variant="detail">
              {t("productDetails:rawData.acp.eligibleSearch")}
            </Text>
            <Badge variant={data.is_eligible_search ? "success" : "secondary"}>
              {data.is_eligible_search
                ? t("productDetails:rawData.acp.yes")
                : t("productDetails:rawData.acp.no")}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Text as="span" variant="detail">
              {t("productDetails:rawData.acp.eligibleCheckout")}
            </Text>
            <Badge
              variant={data.is_eligible_checkout ? "success" : "secondary"}
            >
              {data.is_eligible_checkout
                ? t("productDetails:rawData.acp.yes")
                : t("productDetails:rawData.acp.no")}
            </Badge>
          </div>
        </div>
      </DataSection>

      <DataSection
        title={t("productDetails:rawData.acp.product")}
        testId="section-acp-product"
        actions={<ToggleAllButton allOpen={allOpen} onToggle={toggleAll} />}
      >
        {Object.entries(rest)
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(([key, value]) => (
            <DataSection
              key={key}
              title={humanizeKey(key)}
              testId={`section-acp-${key}`}
              level={1}
              isOpen={!!openState[key]}
              onToggle={() => toggle(key)}
            >
              {renderFields({ [key]: value })}
            </DataSection>
          ))}

        {variantArray.map((variant, index) => {
          const v = variant as Record<string, unknown>;
          const stateKey = `variant-${index}`;
          return (
            <DataSection
              key={String(v.id ?? index)}
              title={`Variant ${index + 1}`}
              testId={`section-acp-variant-${index}`}
              level={1}
              isOpen={!!openState[stateKey]}
              onToggle={() => toggle(stateKey)}
            >
              {renderFields(v)}
            </DataSection>
          );
        })}
      </DataSection>
    </div>
  );
};
